// @flow
import React, { useEffect, useState, type Node } from 'react';
import classnames from 'classnames';

import { getHeroDimensions } from 'Components/widgets/hero/Hero.jsx';
import { ImmersiveSlideShowControls } from 'Components/widgets/immersive-slideshow/controls/ImmersiveSlideShowControls.jsx';

type Props = {
  className?: string,
  immersiveSize?: string,
  items: Array<Node>,
  nextLabel?: string,
  previousLabel?: string,
};

function getInitialDisplayedIndex(length: number) {
  return Array(length)
    .fill(false)
    .map((_, idx) => idx === length - 1 || idx < 2);
}

export function ImmersiveSlideShowLarge(props: Props) {
  const { className, immersiveSize = 'large', items = [], nextLabel, previousLabel } = props;

  const [activeIndex, setActiveIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(getInitialDisplayedIndex(items.length));

  useEffect(
    () => {
      if (displayedIndex.every(Boolean)) return;

      const newDisplayedIndex = displayedIndex.map(
        (isDisplayed, idx) => isDisplayed || idx === activeIndex + 1 || idx === activeIndex - 1,
      );
      setDisplayedIndex(newDisplayedIndex);
    },
    [activeIndex],
  );

  if (!items.length) return null;

  const increase = () => {
    activeIndex === items.length - 1 ? setActiveIndex(0) : setActiveIndex(activeIndex + 1);
  };

  const decrease = () => {
    activeIndex === 0 ? setActiveIndex(items.length - 1) : setActiveIndex(activeIndex - 1);
  };

  const { height } = getHeroDimensions(immersiveSize, true);

  return (
    <div className={classnames(className, 'relative overflow-hidden')} style={{ height }}>
      {items.map((item, idx) => (
        <div
          className={classnames('absolute inset-0 transition-opacity-long', {
            'opacity-100': idx === activeIndex,
            'opacity-0': idx !== activeIndex,
          })}
          key={idx}
        >
          {displayedIndex[idx] ? item : null}
        </div>
      ))}
      {items.length > 1 && (
        <ImmersiveSlideShowControls
          nextLabel={nextLabel}
          onNext={increase}
          onPrev={decrease}
          previousLabel={previousLabel}
        />
      )}
    </div>
  );
}
